
export class DicomTagContents {
	constructor(public groupNum: number, public elementNum: number, public vr: string,
		private valueLength: number | ((explicit: boolean) => number), private serializeCb: (dv: DataView, offset: number, explicit: boolean) => void) {
	}

	public tagString() {
		return '(' + this.groupNum.toString(16).padStart(4, '0') + ',' + this.elementNum.toString(16).padStart(4, '0') + ')';
	}

	public toArrayBuffer(explicit: boolean) {
		let ab = new ArrayBuffer(this.serializedLength(explicit));
		this.serialize(explicit, new DataView(ab), 0);
		return ab;
	}

	public serialize(explicit: boolean, dv: DataView, offset: number) {
		let valueLength = this.valueLengthNumber(explicit);

		if (!explicit) {

			dv.setUint16(offset+0, this.groupNum, true);
			dv.setUint16(offset+2, this.elementNum, true);
			dv.setUint32(offset+4, valueLength, true);

			this.serializeCb(dv, offset+8, explicit);

			return 8 + valueLength;
		} else {
			switch (this.vr) {
				case "OB":
				case "OW":
				case "OF":
				case "SQ":
				case "UT":
				case "UN": {

					dv.setUint16(offset+0, this.groupNum, true);
					dv.setUint16(offset+2, this.elementNum, true);
					dv.setUint8(offset+4, this.vr.codePointAt(0));
					dv.setUint8(offset+5, this.vr.codePointAt(1));
					dv.setUint32(offset+8, valueLength, true);

					this.serializeCb(dv, offset+12, explicit);
					return 12 + valueLength;
				}
				default: {
					if (valueLength > 65535) {
						throw new Error(`Cannot save tag ${this.tagString()} with length of ${valueLength} in explicit VR mode`);
					}

					dv.setUint16(offset+0, this.groupNum, true);
					dv.setUint16(offset+2, this.elementNum, true);
					dv.setUint8(offset+4, this.vr.codePointAt(0));
					dv.setUint8(offset+5, this.vr.codePointAt(1));
					dv.setUint16(offset+6, valueLength, true);

					this.serializeCb(dv, offset+8, explicit);

					return 8 + valueLength;
				}
			}
		}
	}

	private valueLengthNumber(explicit: boolean) {
		let valueLength: number;

		if (typeof this.valueLength === 'number')
			valueLength = this.valueLength;
		else
			valueLength = this.valueLength(explicit);

		// Value lengths must be even
		if (valueLength % 2 === 1)
			valueLength++;

		return valueLength;
	}

	public serializedLength(explicit: boolean) {
		let valueLength = this.valueLengthNumber(explicit);

		if (!explicit) {
			return 8 + valueLength;
		} else {
			switch (this.vr) {
				case "OB":
				case "OW":
				case "OF":
				case "SQ":
				case "UT":
				case "UN":
					return 12 + valueLength;
				default:
					return 8 + valueLength;
			}
		}
	}

	public static toArrayBuffer(tags: DicomTagContents[], explicit: boolean) {
		return tags.map(tag => tag.toArrayBuffer(explicit));
	}

	public static makeGroup(tags: DicomTagContents[], explicit: boolean, forceGroupLength?: boolean) {
		if (tags.length === 0)
			return [];
		
		let group = tags[0].groupNum;
		let sortedTags = [...tags].sort((a, b) => a.elementNum - b.elementNum);
		let array: ArrayBuffer[] = DicomTagContents.toArrayBuffer(sortedTags, explicit);

		// "Group Length (gggg,0000) Standard Data Elements have been retired. See PS3.5-2007."
		// ...but it is still used in the File Meta section
		if (forceGroupLength) {
			let totalLength = array.reduce((acc, ab) => acc + ab.byteLength, 0);

			array.unshift(new DicomTagUL(group, 0).make(totalLength).toArrayBuffer(explicit));
		}

		return array;
	}
}

function serializeLatin1String(value: string, spacePadding?: boolean) {
	return (dv: DataView, offset: number) => {
		for (let i = 0; i < value.length; i++) {
			let pt = value.codePointAt(i);

			if (pt > 255)
				pt = "_".codePointAt(0);

			dv.setUint8(offset+i, pt);
		}

		if (spacePadding) {
			if (value.length % 2 !== 0) {
				dv.setUint8(offset + value.length, 0x20); // space to pad to even byte count
			}
		}
	};
}

export class DicomTagBase {
	constructor(private _groupNum: number, private _elementNum: number) {
	}

	public get groupNum() {
		return this._groupNum;
	}

	public get elementNum() {
		return this._elementNum;
	}

	public get tagString() {
		return this.groupNum.toString(16).padStart(4, '0') + this.elementNum.toString(16).padStart(4, '0');
	}
}

export class DicomTagUI extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: string) {
		return new DicomTagContents(this.groupNum, this.elementNum, "UI", value.length, serializeLatin1String(value));
	}

	public makeJSON(value: string) {
		return {
			[this.tagString]: {
				vr: "UI",
				Value: value,
			}
		};
	}
}

export class DicomTagOB extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: string) {
		return new DicomTagContents(this.groupNum, this.elementNum, "OB", value.length, serializeLatin1String(value));
	}
}

export class DicomTagLO extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: string) {
		return new DicomTagContents(this.groupNum, this.elementNum, "LO", value.length, serializeLatin1String(value, true));
	}

	public makeJSON(value: string) {
		return {
			[this.tagString]: {
				vr: "LO",
				Value: value,
			}
		};
	}
}

export class DicomTagSH extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: string) {
		if (value.length > 16)
			throw new Error("SH is too long");

		return new DicomTagContents(this.groupNum, this.elementNum, "SH", value.length, serializeLatin1String(value, true));
	}

	public makeJSON(value: string) {
		if (value.length > 16)
			throw new Error("SH is too long");

		return {
			[this.tagString]: {
				vr: "SH",
				Value: value,
			}
		};
	}
}

export class DicomTagCS extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: string) {
		if (value.length > 16)
			throw new Error("CS is too long");

		value = value.replace(/[^0-9A-Z_ ]/, '_');
		return new DicomTagContents(this.groupNum, this.elementNum, "CS", value.length, serializeLatin1String(value));
	}

	public makeJSON(value: string) {
		if (value.length > 16)
			throw new Error("CS is too long");

		value = value.replace(/[^0-9A-Z_ ]/, '_');

		return {
			[this.tagString]: {
				vr: "CS",
				Value: value,
			}
		};
	}
}

export class DicomTagUL extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: number) {
		return new DicomTagContents(this.groupNum, this.elementNum, "UL", 4, (dv, offset) => {
			dv.setUint32(offset, value, true);
		});
	}

	public makeJSON(value: number) {
		return {
			[this.tagString]: {
				vr: "UL",
				Value: value,
			}
		};
	}
}

export class DicomTagUS extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: number) {
		return new DicomTagContents(this.groupNum, this.elementNum, "US", 2, (dv, offset) => {
			dv.setUint16(offset, value, true);
		});
	}

	public makeJSON(value: any) {
		return {
			[this.tagString]: {
				vr: "US",
				Value: value,
			}
		};
	}
}

export class DicomTagIS extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: number) {
		return new DicomTagContents(this.groupNum, this.elementNum, "IS", 2, (dv, offset) => {
			dv.setUint16(offset, value, true);
		});
	}

	public makeJSON(value: number) {
		return {
			[this.tagString]: {
				vr: "IS",
				Value: value,
			}
		};
	}
}

export class DicomTagDA extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(date: Date) {
		let str = date.getFullYear().toFixed().padStart(4, '0') + 
			(date.getMonth() + 1).toFixed().padStart(2, '0') +
			date.getDate().toFixed().padStart(2, '0');
		return new DicomTagContents(this.groupNum, this.elementNum, "DA", str.length, serializeLatin1String(str));
	}

	public makeJSON(date: Date | string) {
		const str = (typeof date === 'string') ? date : date.getFullYear().toFixed().padStart(4, '0') + 
			(date.getMonth() + 1).toFixed().padStart(2, '0') +
			date.getDate().toFixed().padStart(2, '0');

		return {
			[this.tagString]: {
				vr: "DA",
				Value: str,
			}
		};
	}
}

export class DicomTagTM extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(date: Date) {
		let str = date.getHours().toFixed().padStart(2, '0') + 
			date.getMinutes().toFixed().padStart(2, '0') +
			date.getSeconds().toFixed().padStart(2, '0');

		return new DicomTagContents(this.groupNum, this.elementNum, "TM", str.length, serializeLatin1String(str));
	}

	public makeJSON(date?: Date) {
		let str = "";
		
		if (date) {
			str = date.getHours().toFixed().padStart(2, '0') + 
				date.getMinutes().toFixed().padStart(2, '0') +
				date.getSeconds().toFixed().padStart(2, '0');
		}

		return {
			[this.tagString]: {
				vr: "TM",
				Value: str,
			}
		};
	}
}

export class DicomTagDS extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(value: number) {
		let str = value.toFixed();
		return new DicomTagContents(this.groupNum, this.elementNum, "DS", str.length, serializeLatin1String(str));
	}

	public makeJSON(value: number) {
		return {
			[this.tagString]: {
				vr: "DS",
				Value: value.toFixed(),
			}
		};
	}
}

export class DicomTagPN extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(surname: string, firstName: string) {
		let str = `${surname}^${firstName}`;
		return this.makeFromDicomString(str);
	}

	public makeFromDicomString(str: string) {
		return new DicomTagContents(this.groupNum, this.elementNum, "PN", str.length, serializeLatin1String(str));
	}

	public makeJSON(value: string) {
		return {
			[this.tagString]: {
				vr: "PN",
				Value: value,
			}
		};
	}
}

export class DicomTagAS extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(ageValue: number, ageUnit: "Y"|"M"|"W"|"D") {
		let str = `${ageValue.toFixed()}${ageUnit}`;
		if (str.length % 2 !== 0)
			str = "0" + str;

		return new DicomTagContents(this.groupNum, this.elementNum, "AS", str.length, serializeLatin1String(str));
	}
}

export class DicomTagSQ extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(...items: DicomTagContents[][]) {
		return new DicomTagContents(this.groupNum, this.elementNum, "SQ", explicit => {
			// https://dicom.nema.org/dicom/2013/output/chtml/part05/sect_7.5.html
			// We use the syntax shown in Table 7.5-1

			let itemPreambles = items.length * 8;
			let dataLength = items.reduce((acc, item) => {
				return acc + item.reduce((acc, tag) => {
					return acc + tag.serializedLength(explicit);
				}, 0);
			}, 0);

			return itemPreambles + dataLength;
		}, (dv, offset, explicit) => {
			for (let i = 0; i < items.length; i++) {
				// Item Tag
				dv.setUint16(offset+0, 0xfffe, true);
				dv.setUint16(offset+2, 0xe000, true);

				let itemLength = items[i].reduce((acc, tag) => acc + tag.serializedLength(explicit), 0);
				dv.setUint32(offset+4, itemLength, true);

				offset += 8;

				for (let j = 0; j < items[i].length; j++) {
					let bytes = items[i][j].serialize(explicit, dv, offset);
					offset += bytes;
				}
			}
		});
	}

	public makeJSON(...items: any[]) {
		return {
			[this.tagString]: {
				vr: "SQ",
				Value: items,
			}
		};
	}
}

export class DicomTagOW extends DicomTagBase {
	constructor(groupNum: number, elementNum: number) {
		super(groupNum, elementNum);
	}

	public make(data: Uint16Array) {
		return new DicomTagContents(this.groupNum, this.elementNum, "OW", data.byteLength, (dv, offset) => {
			new Uint16Array(dv.buffer, offset).set(data);
		});
	}
}

// Merge many DICOM tags created by calling makeJSON into a single object
export function mergeJSONTags(...tags: any[]) {
	let merged = {};
	tags.forEach(tag => {
		Object.keys(tag).forEach(key => {
			merged[key] = tag[key];
		});
	});
	return merged;
}
